// src/lib/ImageLightbox.js
// The project gallery's image viewer, extracted for reuse (bids, etc.).
// Same logic as ProjectProfile_Docs&Media: wheel zooms toward the cursor
// (1x–10x), drag pans when zoomed, one-finger swipe changes photos at 1x,
// two-finger pinch zooms, Esc/backdrop/✕ closes.
//
// Props:
//   images:     [{ name, getUrl: async () => displayUrl }]
//   startIndex: which image to open on
//   onClose:    () => void
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function ImageLightbox({ images, startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [url, setUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [touchActive, setTouchActive] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  // load the current image's display URL
  useEffect(() => {
    let alive = true;
    setUrl(null);
    Promise.resolve(images[index]?.getUrl()).then(u => { if (alive) setUrl(u); }).catch(() => {});
    return () => { alive = false; };
  }, [index, images]);

  // Esc closes; lock body scroll while open
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const navigate = (direction) => {
    if (zoom > 1 || !images.length) return;
    let next = index + direction;
    if (next < 0) next = images.length - 1;
    if (next >= images.length) next = 0;
    setIndex(next);
    resetView();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const scaleAmount = 0.2;
    const delta = e.deltaY > 0 ? -scaleAmount : scaleAmount;
    const nextZoom = Math.max(1, Math.min(10, zoom + delta));
    if (nextZoom === zoom) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const imageRelativeX = (mouseX - centerX - pan.x) / zoom;
    const imageRelativeY = (mouseY - centerY - pan.y) / zoom;
    setZoom(nextZoom);
    if (nextZoom <= 1) {
      setPan({ x: 0, y: 0 });
    } else {
      setPan({
        x: mouseX - centerX - imageRelativeX * nextZoom,
        y: mouseY - centerY - imageRelativeY * nextZoom,
      });
    }
  };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    hasMoved.current = false;
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - lastMousePos.x;
    const dy = e.clientY - lastMousePos.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasMoved.current = true;
    setPan({ x: pan.x + dx, y: pan.y + dy });
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseUp = () => setIsDragging(false);

  // ---- Touch gestures (mobile) ----
  const touchState = useRef({ mode: null, startX: 0, startY: 0, startDist: 0, startZoom: 1, startPan: { x: 0, y: 0 }, lastX: 0, lastY: 0 });

  const handleTouchStart = (e) => {
    setTouchActive(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      touchState.current = {
        ...touchState.current,
        mode: 'pinch',
        startDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        startZoom: zoom,
        startPan: { ...pan },
        startMid: { x: (a.clientX + b.clientX) / 2 - cx, y: (a.clientY + b.clientY) / 2 - cy },
        center: { x: cx, y: cy },
      };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      touchState.current = {
        ...touchState.current,
        mode: zoom > 1 ? 'pan' : 'swipe',
        startX: t.clientX, startY: t.clientY,
        lastX: t.clientX, lastY: t.clientY,
        startPan: { ...pan },
      };
    }
    hasMoved.current = false;
  };

  const handleTouchMove = (e) => {
    const st = touchState.current;
    if (st.mode === 'pinch' && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const next = Math.max(1, Math.min(10, st.startZoom * (dist / (st.startDist || 1))));
      setZoom(next);
      if (next <= 1) {
        setPan({ x: 0, y: 0 });
      } else {
        const mid = { x: (a.clientX + b.clientX) / 2 - st.center.x, y: (a.clientY + b.clientY) / 2 - st.center.y };
        const s = next / (st.startZoom || 1);
        setPan({
          x: mid.x - s * (st.startMid.x - st.startPan.x),
          y: mid.y - s * (st.startMid.y - st.startPan.y),
        });
      }
      hasMoved.current = true;
    } else if (st.mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      setPan({ x: st.startPan.x + (t.clientX - st.startX), y: st.startPan.y + (t.clientY - st.startY) });
      hasMoved.current = true;
    } else if (st.mode === 'swipe' && e.touches.length === 1) {
      const t = e.touches[0];
      st.lastX = t.clientX;
      st.lastY = t.clientY;
      if (Math.abs(t.clientX - st.startX) > 10) hasMoved.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    const st = touchState.current;
    // one finger lifted after a pinch → continue as a pan
    if (e.touches && e.touches.length === 1) {
      const t = e.touches[0];
      touchState.current = {
        ...touchState.current,
        mode: zoom > 1 ? 'pan' : 'swipe',
        startX: t.clientX, startY: t.clientY,
        lastX: t.clientX, lastY: t.clientY,
        startPan: { ...pan },
      };
      return;
    }
    if (st.mode === 'swipe') {
      const dx = st.lastX - st.startX;
      const dy = st.lastY - st.startY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        navigate(dx < 0 ? 1 : -1);
      }
    }
    touchState.current.mode = null;
    setTouchActive(false);
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="fixed inset-0 z-[9999] w-screen h-screen max-md:h-[100dvh] bg-black/95 shadow-2xl flex items-center justify-center overflow-hidden"
        onClick={() => { if (!hasMoved.current) onClose(); }}
      >
        <button
          className="absolute top-6 right-6 text-white/50 hover:text-white bg-white/10 p-3 rounded-full z-50 transition-all"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
        >
          <X size={24} />
        </button>

        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full z-50 border border-white/10">
          {Math.round(zoom * 100)}%
        </div>

        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            className={`absolute left-4 p-4 text-white/30 hover:text-white z-50 transition-opacity ${zoom > 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            disabled={zoom > 1}
          >
            <ChevronLeft size={40} />
          </button>
        )}

        <div
          className="relative w-full h-full flex items-center justify-center overflow-hidden"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default', touchAction: 'none' }}
        >
          {url ? (
            <img
              src={url}
              alt={images[index]?.name || 'Full size'}
              className="max-w-full max-h-full object-contain pointer-events-none"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center',
                transition: (isDragging || touchActive) ? 'none' : 'transform 0.1s ease-out',
                willChange: 'transform',
              }}
              draggable={false}
            />
          ) : (
            <p className="text-white/40 text-sm animate-pulse">Loading…</p>
          )}
        </div>

        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
            className={`absolute right-4 p-4 text-white/30 hover:text-white z-50 transition-opacity ${zoom > 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            disabled={zoom > 1}
          >
            <ChevronRight size={40} />
          </button>
        )}

        {images[index]?.name && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-black/70 text-zinc-300 text-[11px] px-3 py-1 rounded-full z-50 border border-white/10 max-w-[80vw] truncate">
            {images[index].name}{images.length > 1 ? ` · ${index + 1}/${images.length}` : ''}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
